import { useCallback, useMemo, useRef, useState } from 'react';

import {
  useGetHealthCheckRuleConfigs,
  useGetHealthCheckRuleConfigTypes,
} from '../../hooks/reactQuerys/HealthCheckRuleQueries';
import { IHealthCheckRuleOverride } from '../../Interfaces/IHealthCheckRule';
import { HEALTH_CHECK_CATALOG } from './catalog';
import { resolveActiveRules } from './resolveActiveRules';
import { EMPTY_HEALTH_CHECK_RESULT, runHealthCheck } from './runHealthCheck';
import { IHealthCheckInput } from './types';

interface IArgs {
  idLegalTransactionType: number;
  legalTransactionTypeName?: string;
  /** Abweichungen, wie sie mit dem Vorgang geladen wurden. */
  initialOverrideArray: IHealthCheckRuleOverride[];
  /**
   * Freischaltung des angemeldeten Benutzers (/admin/dev/healthcheck). Ohne
   * sie werden keine Regeln geladen; der Aufrufer führt dann keinen Check aus.
   */
  enabled?: boolean;
}

/**
 * Bindet Konfiguration, Vorgangs-Abweichungen und Ausführung zusammen (#1283).
 *
 * Die Abweichungen liegen als lokaler Zustand hier und reisen beim Speichern im
 * `fullObject` mit — sie werden also nicht sofort persistiert. Das ist Absicht:
 * eine Änderung muss den nächsten Health Check schon beeinflussen, bevor
 * gespeichert wird, und sie soll wie jede andere Änderung im Vorgang
 * zurücknehmbar sein, indem man nicht speichert.
 */
export const useHealthCheck = ({
  idLegalTransactionType,
  legalTransactionTypeName,
  initialOverrideArray,
  enabled = true,
}: IArgs) => {
  const { healthCheckRuleConfigs, isLoadingHealthCheckRuleConfigs } =
    useGetHealthCheckRuleConfigs({ enabled });
  const { healthCheckRuleConfigTypes, isLoadingHealthCheckRuleConfigTypes } =
    useGetHealthCheckRuleConfigTypes({ enabled });

  const [overrideArray, setOverrideArray] =
    useState<IHealthCheckRuleOverride[]>(initialOverrideArray);
  const [result, setResult] = useState(EMPTY_HEALTH_CHECK_RESULT);

  /**
   * Letzte Eingabe, damit ein Umschalten einer Regel die Anzeige sofort
   * aktualisiert. Ohne das bliebe ein Fund stehen, dessen Regel gerade
   * abgeschaltet wurde.
   */
  const lastInput = useRef<IHealthCheckInput | null>(null);

  const ruleStates = useMemo(
    () =>
      resolveActiveRules({
        configArray: healthCheckRuleConfigs,
        configTypeArray: healthCheckRuleConfigTypes,
        overrideArray,
        idLegalTransactionType,
        legalTransactionTypeName,
      }),
    [
      healthCheckRuleConfigs,
      healthCheckRuleConfigTypes,
      overrideArray,
      idLegalTransactionType,
      legalTransactionTypeName,
    ]
  );

  const runCheck = useCallback(
    (input: IHealthCheckInput) => {
      lastInput.current = input;
      const nextResult = runHealthCheck(input, ruleStates);
      setResult(nextResult);
      return nextResult;
    },
    [ruleStates]
  );

  /** Setzt die Anzeige zurück, ohne die Abweichungen anzutasten. */
  const clearResult = useCallback(() => {
    setResult(EMPTY_HEALTH_CHECK_RESULT);
  }, []);

  const rerun = useCallback(
    (nextOverrideArray: IHealthCheckRuleOverride[]) => {
      if (lastInput.current === null) {
        return;
      }

      setResult(
        runHealthCheck(
          lastInput.current,
          resolveActiveRules({
            configArray: healthCheckRuleConfigs,
            configTypeArray: healthCheckRuleConfigTypes,
            overrideArray: nextOverrideArray,
            idLegalTransactionType,
            legalTransactionTypeName,
          })
        )
      );
    },
    [
      healthCheckRuleConfigs,
      healthCheckRuleConfigTypes,
      idLegalTransactionType,
      legalTransactionTypeName,
    ]
  );

  /**
   * Schaltet eine Regel für diesen Vorgang ein oder aus.
   *
   * Stimmt der gewünschte Zustand mit der globalen Vorgabe überein, wird die
   * Abweichung entfernt statt eine redundante Zeile zu schreiben — sonst
   * sammelten sich Zeilen an, die nichts bewirken, und die Herkunftsanzeige
   * würde „für diesen Vorgang geändert" behaupten, wo nichts geändert wurde.
   */
  const toggleRule = useCallback(
    (ruleKey: string, isActive: boolean) => {
      const withoutOverride = resolveActiveRules({
        configArray: healthCheckRuleConfigs,
        configTypeArray: healthCheckRuleConfigTypes,
        overrideArray: [],
        idLegalTransactionType,
        legalTransactionTypeName,
      }).find(state => state.ruleKey === ruleKey);

      setOverrideArray(current => {
        const next =
          withoutOverride !== undefined && withoutOverride.isActive === isActive
            ? current.filter(entry => entry.RuleKey !== ruleKey)
            : [
                ...current.filter(entry => entry.RuleKey !== ruleKey),
                {
                  idHealthCheckRuleLegalTransaction: -1,
                  RuleKey: ruleKey,
                  IsActive: isActive,
                  idUser: null,
                },
              ];

        rerun(next);
        return next;
      });
    },
    [
      healthCheckRuleConfigs,
      healthCheckRuleConfigTypes,
      idLegalTransactionType,
      legalTransactionTypeName,
      rerun,
    ]
  );

  /** Entfernt die Abweichung, der Vorgang folgt wieder der globalen Vorgabe. */
  const resetRule = useCallback(
    (ruleKey: string) => {
      setOverrideArray(current => {
        const next = current.filter(entry => entry.RuleKey !== ruleKey);
        rerun(next);
        return next;
      });
    },
    [rerun]
  );

  /** Setzt die Abweichungen auf den Stand eines neu geladenen Vorgangs. */
  const resetOverrideArray = useCallback(
    (nextOverrideArray: IHealthCheckRuleOverride[]) => {
      setOverrideArray(nextOverrideArray);
    },
    []
  );

  return {
    ruleStates,
    result,
    runCheck,
    clearResult,
    overrideArray,
    toggleRule,
    resetRule,
    resetOverrideArray,
    isLoadingHealthCheck:
      isLoadingHealthCheckRuleConfigs || isLoadingHealthCheckRuleConfigTypes,
  };
};

/**
 * Ergänzt die Regeltitel für den Log-Eintrag (#1330). Transient — der Titel
 * steht im Katalog des Frontends, das Backend kennt nur den Schlüssel und
 * braucht ihn nur, um den Protokolltext zu formulieren.
 *
 * Bewusst erst beim Absenden und nicht im Save-Objekt: sonst würde der
 * Änderungsvergleich des Dialogs dauerhaft anschlagen.
 */
export const withRuleTitles = (overrideArray: IHealthCheckRuleOverride[]) =>
  overrideArray.map(override => ({
    ...override,
    RuleTitle:
      HEALTH_CHECK_CATALOG[override.RuleKey]?.title ?? override.RuleKey,
  }));
